import { Mail, Phone, MapPin, MessageCircle, Instagram, Linkedin } from "lucide-react";
import { CLINIC, buildMailto, buildWhatsApp } from "@/constants/clinic";
import { CONTACT } from "@/constants/testIds/clinic";

export default function ContactPage() {
    return (
        <section className="ihx-warm-surface ihx-grid-overlay" data-testid={CONTACT.page}>
            <div className="ihx-container py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12">
                <div className="lg:col-span-7">
                    <span className="ihx-eyebrow">Contact</span>
                    <h1 className="ihx-h-display mt-5 max-w-2xl">
                        Reach the clinic, <span className="ihx-italic">directly.</span>
                    </h1>
                    <p className="ihx-lead mt-7 max-w-xl">
                        For consultation enquiries, the Discovery Form is the fastest
                        route — your responses are pre-attached to the team’s inbox. For
                        everything else, the channels below reach the practice directly.
                    </p>

                    <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <a
                            data-testid={CONTACT.emailLink}
                            href={buildMailto("Hello from your website", "")}
                            className="ihx-card p-6 flex items-start gap-4"
                        >
                            <span className="w-10 h-10 rounded-full grid place-items-center bg-[hsl(var(--brand-gold-soft)/0.4)] text-[hsl(var(--brand-gold-deep))]">
                                <Mail size={18} strokeWidth={1.6} />
                            </span>
                            <div>
                                <div className="text-[11px] tracking-[0.22em] uppercase text-[hsl(var(--ink-muted))]">
                                    Email
                                </div>
                                <div className="font-serif text-[18px] mt-1 break-all">
                                    {CLINIC.email}
                                </div>
                            </div>
                        </a>
                        <a
                            data-testid={CONTACT.phoneLink}
                            href={`tel:${CLINIC.phone.replace(/\s+/g, "")}`}
                            className="ihx-card p-6 flex items-start gap-4"
                        >
                            <span className="w-10 h-10 rounded-full grid place-items-center bg-[hsl(var(--brand-gold-soft)/0.4)] text-[hsl(var(--brand-gold-deep))]">
                                <Phone size={18} strokeWidth={1.6} />
                            </span>
                            <div>
                                <div className="text-[11px] tracking-[0.22em] uppercase text-[hsl(var(--ink-muted))]">
                                    Direct Line
                                </div>
                                <div className="font-serif text-[18px] mt-1">{CLINIC.phone}</div>
                            </div>
                        </a>
                        <a
                            data-testid={CONTACT.whatsappLink}
                            href={buildWhatsApp("Hello Dr. Ishani — I’d like to learn more about consultations.")}
                            target="_blank"
                            rel="noreferrer noopener"
                            className="ihx-card p-6 flex items-start gap-4"
                        >
                            <span className="w-10 h-10 rounded-full grid place-items-center bg-[hsl(var(--brand-gold-soft)/0.4)] text-[hsl(var(--brand-gold-deep))]">
                                <MessageCircle size={18} strokeWidth={1.6} />
                            </span>
                            <div>
                                <div className="text-[11px] tracking-[0.22em] uppercase text-[hsl(var(--ink-muted))]">
                                    WhatsApp
                                </div>
                                <div className="font-serif text-[18px] mt-1">{CLINIC.phone}</div>
                            </div>
                        </a>
                        <div className="ihx-card p-6 flex items-start gap-4">
                            <span className="w-10 h-10 rounded-full grid place-items-center bg-[hsl(var(--brand-gold-soft)/0.4)] text-[hsl(var(--brand-gold-deep))]">
                                <MapPin size={18} strokeWidth={1.6} />
                            </span>
                            <div>
                                <div className="text-[11px] tracking-[0.22em] uppercase text-[hsl(var(--ink-muted))]">
                                    Clinics
                                </div>
                                <div className="font-serif text-[18px] mt-1">
                                    {CLINIC.cities.join(" · ")}
                                </div>
                                <div className="text-[13px] text-[hsl(var(--ink-muted))] mt-1">
                                    Virtual consultations worldwide
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="mt-12">
                        <p className="text-[11px] tracking-[0.22em] uppercase text-[hsl(var(--ink-muted))] mb-4">
                            Follow the practice
                        </p>
                        <div className="flex items-center gap-3">
                            <a
                                data-testid={CONTACT.instagramLink}
                                href={CLINIC.social.instagram}
                                target="_blank"
                                rel="noreferrer noopener"
                                aria-label="Instagram"
                                className="w-11 h-11 grid place-items-center rounded-full border border-[hsl(var(--border-stone))] text-[hsl(var(--ink-soft))] hover:text-[hsl(var(--brand-ink-deep))] hover:border-[hsl(var(--brand-ink-deep))] transition-colors"
                            >
                                <Instagram size={18} strokeWidth={1.6} />
                            </a>
                            <a
                                data-testid={CONTACT.linkedinLink}
                                href={CLINIC.social.linkedin}
                                target="_blank"
                                rel="noreferrer noopener"
                                aria-label="LinkedIn"
                                className="w-11 h-11 grid place-items-center rounded-full border border-[hsl(var(--border-stone))] text-[hsl(var(--ink-soft))] hover:text-[hsl(var(--brand-ink-deep))] hover:border-[hsl(var(--brand-ink-deep))] transition-colors"
                            >
                                <Linkedin size={18} strokeWidth={1.6} />
                            </a>
                        </div>
                    </div>
                </div>

                <aside className="lg:col-span-5">
                    <div
                        className="rounded-[28px] p-8 md:p-10 text-[hsl(var(--bg-chalk))]"
                        style={{
                            background:
                                "radial-gradient(80% 100% at 100% 0%, hsl(var(--brand-gold) / 0.35) 0%, transparent 55%), hsl(var(--brand-ink-deep))",
                        }}
                    >
                        <span className="ihx-eyebrow" style={{ color: "hsl(var(--brand-gold-soft))" }}>Clinical hours</span>
                        <h2 className="font-serif text-[30px] mt-4 text-[hsl(var(--bg-chalk))]">Response window</h2>
                        <div className="mt-6 divide-y divide-[hsl(var(--bg-chalk))]/15">
                            {[
                                { d: "Mon — Fri", t: "10:00 – 19:00 IST" },
                                { d: "Saturday", t: "10:00 – 14:00 IST (virtual only)" },
                                { d: "Sunday", t: "Closed" },
                            ].map((r) => (
                                <div key={r.d} className="flex items-center justify-between py-3 text-[14.5px]">
                                    <span className="text-[hsl(var(--bg-chalk))]/70">{r.d}</span>
                                    <span>{r.t}</span>
                                </div>
                            ))}
                        </div>
                        <p className="text-[13px] text-[hsl(var(--bg-chalk))]/60 leading-[1.7] mt-6">
                            Discovery Form submissions are reviewed personally — expect a
                            considered reply within 24 working hours. For medical emergencies,
                            please contact your local emergency services.
                        </p>
                    </div>
                </aside>
            </div>
        </section>
    );
}
